import { isAxiosError } from 'axios';
import type { ErrorResponse } from '@/types/error';
import type {
  EvaSyncPreflightResponse,
  EvaSyncRunCountersView,
  EvaSyncRunListing,
  EvaSyncRunView,
} from '@/types/evaSync';

export const DEFAULT_EVA_SYNC_RUN_COUNTERS: EvaSyncRunCountersView = {
  groupsTotal: 0,
  groupsCreated: 0,
  groupsExisting: 0,
  membersTotal: 0,
  membersCreated: 0,
  membersExisting: 0,
  membersFailed: 0,
  roleWarnings: 0,
};

const isEvaSyncRunCountersView = (
  counters: EvaSyncRunCountersView | Record<string, unknown>
): counters is EvaSyncRunCountersView => 'groupsTotal' in counters;

export const mapEvaSyncRunViewToListing = (run: EvaSyncRunView): EvaSyncRunListing => ({
  id: run.id,
  evaPublicId: run.evaPublicId,
  status: run.status,
  bulletinMode: run.bulletinMode,
  hasReport: !!run.reportPath,
  counters: isEvaSyncRunCountersView(run.counters) ? run.counters : DEFAULT_EVA_SYNC_RUN_COUNTERS,
  reportPath: run.reportPath,
  errorCode: run.errorCode,
  errorMessage: run.errorMessage,
  startedAt: run.startedAt,
  finishedAt: run.finishedAt,
  createdDate: run.startedAt ?? new Date().toISOString(),
});

export const EVA_SYNC_BULLETIN_PROPERTY = 'bulletin';

export const EVA_SYNC_MPA_BULLETIN_NOT_CONFIGURED_MESSAGE = 'MPA bulletin is not configured.';

export const isMpaBulletinNotConfiguredPreflightError = (error: unknown): boolean => {
  if (!isAxiosError<ErrorResponse>(error)) {
    return false;
  }

  const fieldErrors = error.response?.data?.errors ?? [];

  return fieldErrors.some(
    (fieldError) =>
      fieldError.property === EVA_SYNC_BULLETIN_PROPERTY &&
      fieldError.messages.includes(EVA_SYNC_MPA_BULLETIN_NOT_CONFIGURED_MESSAGE)
  );
};

export const shouldConfirmEvaSyncWithoutBulletin = ({
  preflightData,
  preflightError,
}: {
  preflightData: EvaSyncPreflightResponse | undefined;
  preflightError: unknown;
}): boolean => {
  if (isMpaBulletinNotConfiguredPreflightError(preflightError)) {
    return true;
  }

  if (!preflightData) {
    return false;
  }

  return !preflightData.bulletin.mpaConfigured;
};
